import { Slider } from '@mui/material';
import { useEffect, useRef, useState } from 'react';

import { PostingType } from '@/api/influencer';
import { ReactComponent as MusicIcon } from '@/assets/icons/bx-music.svg';
import InstagramIcon from '@/assets/instagram.png';
import XIcon from '@/assets/TwitterLogo.png';
import YoutubeIcon from '@/assets/youtube.png';

import { ReactComponent as BookarkIcon } from './assets/Bookmark.svg';
import { ReactComponent as HeartTwitterIcon } from './assets/bx-heart-twitter.svg';
import { ReactComponent as HeartIcon } from './assets/bx-heart.svg';
import { ReactComponent as MessageIcon } from './assets/bx-message-rounded.svg';
import { ReactComponent as PaperPlaneIcon } from './assets/bx-paper-plane.svg';
import { ReactComponent as VideoPauseIcon } from './assets/bx-pause.svg';
import { ReactComponent as VideoPlayIcon } from './assets/bx-play.svg';
import { ReactComponent as RepeatIcon } from './assets/bx-repeat.svg';
import { ReactComponent as ArrowAltIcon } from './assets/bx-up-arrow-alt.svg';
import InstaReelFrame from './assets/instagram_reel_frame.png';
import InstaStoryBar from './assets/instagram_story_bar.png';
import InstaStoryIcon from './assets/instagram_story_icon.png';
import InstaStoryMessage from './assets/instagram_story_message.png';
import TiktokFrame from './assets/tiktok_frame.png';
import { ReactComponent as VolumeUpIcon } from './assets/volume-up.svg';
import YoutubeShortFrame from './assets/youtube_short_frame.png';

interface Props {
  postingType: PostingType;
  imagePath: string;
  accountName: string;
  contentText: string;
  postingDate: string;
  movieUrl?: string;
}

interface InstagramProps {
  imagePath: string;
  accountName: string;
  contentText: string;
  postingDate: string;
  movieUrl?: string;
}

interface VideoProps {
  mediaPath: string;
  children?: React.ReactNode;
}

const VideoPlayBar = ({
  currentInSec,
  handleSeekbarValue,
  currentVolume,
  handleSeekbarVolume,
  durationInSec,
  isPlaying,
  handlePlayPause,
}: {
  currentInSec: number;
  handleSeekbarValue: (value: number) => void;
  currentVolume: number;
  handleSeekbarVolume: (value: number) => void;
  durationInSec: number;
  isPlaying: boolean;
  handlePlayPause: () => void;
}) => {
  const current = sec2min(currentInSec);
  const duration = sec2min(durationInSec);

  function sec2min(sec: number) {
    const min = Math.floor(sec / 60);
    const secrem = Math.floor(sec % 60);
    return {
      min: min,
      sec: secrem,
    };
  }

  function zeroPadding(num: number, len: number) {
    if (len < num.toString().length) {
      console.error('指定桁数よりも大きな数字が入力されました。');
      return num;
    }
    return (Array(len).join('0') + num).slice(-len);
  }

  return (
    <>
      <div className="flex w-[245px] items-center justify-between">
        {isPlaying ? (
          <button onClick={handlePlayPause} className="h-[24px] w-[24px] flex-initial rounded-full bg-[#007CC2]">
            <VideoPauseIcon />
          </button>
        ) : (
          <button onClick={handlePlayPause} className="h-[24px] w-[24px] flex-initial rounded-full bg-[#007CC2]">
            <VideoPlayIcon />
          </button>
        )}
        <Slider
          className="ml-3 flex-auto"
          value={currentInSec}
          min={0}
          max={durationInSec}
          onChange={(event: Event, value: number | number[]) => {
            handleSeekbarValue(typeof value === 'number' ? value : value[0]);
          }}
          step={0.2}
        />
        <p className="ml-3 flex-initial text-xs font-medium text-[#6B7280]">
          {duration.min - current.min}:{zeroPadding(duration.sec - current.sec, 2)}
        </p>
      </div>
      <div className="flex w-[122px] items-center justify-between">
        <div className="w-[22px] flex-initial">
          <VolumeUpIcon />
        </div>
        <Slider
          className="ml-3 flex-auto"
          value={currentVolume}
          min={0}
          max={1}
          onChange={(event: Event, value: number | number[]) => {
            handleSeekbarVolume(typeof value === 'number' ? value : value[0]);
          }}
          step={0.1}
          sx={{ color: '#6B7280' }}
        />
      </div>
    </>
  );
};

const ContentVideoPlayer = ({ mediaPath, children }: VideoProps) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentInSec, setCurrentInSec] = useState(0);
  const [durationInSec, setDurationInSec] = useState(0);
  const [currentVolume, setCurrentVolume] = useState(0.5);

  const videoRef = useRef<HTMLVideoElement>(null);

  const handlePlayPause = () => {
    if (videoRef.current) {
      isPlaying ? videoRef.current.pause() : videoRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      console.log(`ビデオの長さ: ${videoRef.current.duration}秒`);
      setDurationInSec(videoRef.current.duration);
    } else {
      console.error('ビデオのメタデータを取得不能');
    }
  };

  const handleSeekbarValue = (value: number) => {
    setCurrentInSec(value);
    if (videoRef.current) {
      videoRef.current.currentTime = value;
    }
  };

  const handleSeekbarVolume = (value: number) => {
    setCurrentVolume(value);
    if (videoRef.current) {
      videoRef.current.volume = value;
    }
  };

  useEffect(() => {
    if (isPlaying) {
      const interval = setInterval(() => {
        if (videoRef.current) {
          setCurrentInSec(videoRef.current.currentTime);
        }
      }, 200);
      return function () {
        clearInterval(interval);
      };
    }
  }, [isPlaying]);

  useEffect(() => {
    setCurrentInSec(0);
    setCurrentVolume(0.5);
    setDurationInSec(0);
  }, [mediaPath]);

  return (
    <>
      <div className="relative mb-[20px] aspect-[9/16] w-[239px] overflow-hidden">
        {mediaPath.includes('mp4') ? (
          <video
            ref={videoRef}
            className="h-full"
            src={mediaPath}
            onClick={handlePlayPause}
            onLoadedMetadata={handleLoadedMetadata}
          >
            <track kind="captions" />
          </video>
        ) : (
          <img src={mediaPath} alt="" className="h-full" />
        )}
        {children}
      </div>
      {mediaPath.includes('mp4') && (
        <>
          <VideoPlayBar
            currentInSec={currentInSec}
            handleSeekbarValue={handleSeekbarValue}
            currentVolume={currentVolume}
            handleSeekbarVolume={handleSeekbarVolume}
            durationInSec={durationInSec}
            isPlaying={isPlaying}
            handlePlayPause={handlePlayPause}
          />
        </>
      )}
    </>
  );
};

const InstagramFeed = (props: InstagramProps) => {
  return (
    <div className="w-[290px]">
      <div className="flex w-[290px] items-center justify-between rounded-t-sm border-x border-t border-x-gray-200 border-t-gray-200 px-[6px] py-[13px]">
        <img src={InstagramIcon} alt="" className="aspect-square w-[20px] p-1" />
        <span className="ml-1 w-full text-xs">{props.accountName}</span>
      </div>
      <div className="w-[290px]">
        {props.imagePath.includes('mp4') ? (
          <video controls className="w-[290px]" src={props.imagePath}>
            <track kind="captions" />
          </video>
        ) : (
          <img src={props.imagePath} alt="" className="w-[290px]" />
        )}
      </div>
      <div className="flex h-[100px] w-[290px] items-center justify-center rounded-b-sm border-x border-b border-x-gray-200 border-b-gray-200">
        <div className="mb-[13px] mt-[6px] w-[280px]">
          <div className="mb-[9px] flex w-[275px] items-center justify-between">
            <div className="flex w-[66px] items-center justify-between">
              <HeartIcon fill="#000000" />
              <MessageIcon fill="#000000" />
              <PaperPlaneIcon fill="#000000" />
            </div>
            <BookarkIcon />
          </div>
          <div className="w-[280px]">
            <textarea
              className="mb-[4px] h-[23px] w-full resize-none overflow-hidden border-none bg-transparent p-0 text-[8px] font-normal leading-3"
              disabled
            >
              {props.contentText}
            </textarea>
            <div className="mb-[4px] h-[13px] w-[280px] text-[9px] font-medium text-gray-300">続きを読む</div>
            <div className="h-[13px] w-[280px] text-[9px] font-medium text-gray-400">{props.postingDate}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

const InstagramReel = (props: InstagramProps) => {
  return (
    <div className="">
      <ContentVideoPlayer mediaPath={props.imagePath || ''}>
        <p className="absolute left-[14px] top-[14px] text-sm font-medium leading-none text-white">Reels</p>
        <div className="absolute bottom-3 left-3 w-[120px]">
          <div className="mb-[6px] flex items-center justify-between">
            <img src={InstagramIcon} alt="" className="aspect-square w-[20px] rounded-full bg-white p-1" />
            <span className="ml-1 w-full text-[6px] text-white">{props.accountName}</span>
          </div>
          <p className="h-[12px] w-full text-[8px] leading-tight text-white opacity-80">
            {props.contentText.length > 12 ? props.contentText.substring(0, 12) : props.contentText}
          </p>
          <p className="h-[12px] text-center text-[8px] font-medium leading-none text-white opacity-50">...more</p>
          <div className="flex items-center justify-between">
            <ArrowAltIcon fill="#FFFFFF" />
            <span className="ml-1 w-full text-[8px] text-white">Lalalala.music - LIddell</span>
          </div>
        </div>
        <div className="absolute bottom-3 right-3 flex h-[151px] items-center justify-center">
          <img src={InstaReelFrame} alt="" className="h-full w-full" />
        </div>
      </ContentVideoPlayer>
    </div>
  );
};

const InstagramStory = (props: InstagramProps) => {
  return (
    <div className="">
      <ContentVideoPlayer mediaPath={props.imagePath || ''}>
        <div className="absolute top-4 flex w-full items-center justify-center">
          <img src={InstaStoryBar} alt="" className="w-[223px]" />
        </div>
        <div className="absolute top-[22px] w-full">
          <div className="mx-auto mb-[6px] flex w-[223px] items-center justify-between">
            <div className="flex flex-1 items-center">
              <img src={InstagramIcon} alt="" className="aspect-square w-[20px] rounded-full bg-white p-1" />
              <div className="flex flex-auto items-center justify-start">
                <span className="ml-1 text-[6px] text-white">{props.accountName}</span>
                <span className="ml-1 text-[6px] text-white opacity-60">１時間前</span>
              </div>
            </div>
            <div className="h-[15px] flex-initial">
              <img src={InstaStoryIcon} alt="" className="h-full" />
            </div>
          </div>
        </div>
        <div className="absolute bottom-3 flex w-full items-center justify-center">
          <img src={InstaStoryMessage} alt="" className="w-[223px]" />
        </div>
      </ContentVideoPlayer>
    </div>
  );
};

const XImagePost = (props: InstagramProps) => {
  return (
    <div className="w-[294px] border border-gray-200 px-[8px] py-[16px]">
      <div className="mb-[5px]">
        <div className="mb-[10px] flex items-center">
          <img src={XIcon} alt="" className="mr-[5px] aspect-square w-[20px] p-1" />
          <span className="mr-[5px] whitespace-nowrap text-[9px] font-medium">{props.accountName}</span>
        </div>
        <div className="h-[40px] w-full">
          <textarea
            className="h-full w-full resize-none overflow-hidden border-none bg-transparent p-0 text-[8px] font-normal leading-3"
            disabled
          >
            {props.contentText}
          </textarea>
        </div>
      </div>
      <div className="mb-[5px] w-[276px]">
        {props.imagePath.includes('mp4') ? (
          <video controls className="w-[276px]" src={props.imagePath}>
            <track kind="captions" />
          </video>
        ) : (
          <img src={props.imagePath} alt="" className="w-[276px]" />
        )}
      </div>
      <p className="mb-[5px] text-[8px] font-medium leading-3 text-gray-400">{props.postingDate}</p>
      <div className="mb-[5px] w-[279px] border-t border-gray-200"></div>
      <div className="flex h-[13px] w-[70px] items-center justify-between">
        <div className="flex h-[13px] w-[20px] items-start justify-between">
          <MessageIcon fill="#9CA3AF" />
          <span className="text-[8px] font-medium leading-3 text-gray-400">1</span>
        </div>
        <div className="flex h-[13px] w-[21px] items-start justify-between">
          <RepeatIcon fill="#9CA3AF" />
          <span className="text-[8px] font-medium leading-3 text-gray-400">1</span>
        </div>
        <div className="flex h-[13px] w-[21px] items-start justify-between">
          <HeartTwitterIcon fill="#9CA3AF" />
          <span className="text-[8px] font-medium leading-3 text-gray-400">1</span>
        </div>
      </div>
    </div>
  );
};

const XPost = (props: InstagramProps) => {
  return (
    <div className="w-[283px]">
      <div className="mb-[5px]">
        <div className="mb-[10px] flex items-center">
          <img src={XIcon} alt="" className="mr-[5px] w-[20px] p-1" />
          <span className="text-[9px] font-medium">{props.accountName}</span>
        </div>
        <div className="mb-[5px] max-h-[197px] w-[283px]">
          <textarea
            disabled
            value={props.contentText}
            className="h-[197px] w-full resize-none border-none bg-transparent p-0 text-[8px] font-normal leading-3 text-black"
          />
        </div>
      </div>
      <p className="mb-[5px] text-[8px] font-medium leading-3 text-gray-400">{props.postingDate}</p>
      <div className="mb-[5px] w-[279px] border-t border-gray-200"></div>
      <div className="flex h-[13px] w-[70px] items-center justify-between">
        <div className="flex h-[13px] w-[20px] items-start justify-between">
          <MessageIcon fill="#9CA3AF" />
          <span className="text-[8px] font-medium leading-3 text-gray-400">1</span>
        </div>
        <div className="flex h-[13px] w-[21px] items-start justify-between">
          <RepeatIcon fill="#9CA3AF" />
          <span className="text-[8px] font-medium leading-3 text-gray-400">1</span>
        </div>
        <div className="flex h-[13px] w-[21px] items-start justify-between">
          <HeartTwitterIcon fill="#9CA3AF" />
          <span className="text-[8px] font-medium leading-3 text-gray-400">1</span>
        </div>
      </div>
    </div>
  );
};

const Youtube = (props: InstagramProps) => {
  return (
    <div className="w-[270px]">
      <div className="ml-4">
        <p className="mb-1 text-sm font-semibold text-gray-800">Youtubeの動画URL</p>
        <p className="text-xs font-normal text-gray-800">{props.movieUrl || ''}</p>
      </div>
    </div>
  );
};

const YoutubeShort = (props: InstagramProps) => {
  return (
    <div className="">
      <ContentVideoPlayer mediaPath={props.imagePath || ''}>
        <p className="absolute left-[14px] top-[14px] text-sm font-medium leading-none text-white">ショート</p>
        <div className="absolute bottom-3 left-3 w-[120px]">
          <div className="mb-[6px] flex items-center">
            <img src={YoutubeIcon} alt="" className="aspect-square w-[20px] rounded-full bg-white p-1" />
            <span className="ml-1 text-[6px] text-white">{props.accountName}</span>
            <span className="ml-1 w-[48px] flex-initial rounded-sm bg-[#E34A3E] py-1 text-center text-[5px] font-normal leading-none text-white">
              チャンネル登録
            </span>
          </div>
          <p className="h-[12px] w-full text-[8px] leading-tight text-white opacity-80">
            {props.contentText.length > 12 ? props.contentText.substring(0, 12) : props.contentText}
          </p>
        </div>
        <div className="absolute bottom-3 right-3 flex h-[192px] items-center justify-center">
          <img src={YoutubeShortFrame} alt="" className="h-full w-full" />
        </div>
      </ContentVideoPlayer>
    </div>
  );
};

const Tiktok = (props: InstagramProps) => {
  return (
    <div className="">
      <ContentVideoPlayer mediaPath={props.imagePath || ''}>
        <div className="absolute inset-x-0 top-[14px] m-auto flex w-[109px] items-center justify-between">
          <p className="flex flex-col items-center text-[10px] font-semibold leading-none text-white">
            <span className="py-[2px]">フォロー中</span>
            <span className="w-2/3 border"></span>
          </p>
          <p className="py-[2px] text-[10px] font-semibold leading-none text-white opacity-80">おすすめ</p>
        </div>
        <div className="absolute bottom-3 left-3 w-[120px]">
          <div className="mb-[6px] flex items-center justify-between">
            <span className="ml-1 w-full text-xs text-white">{props.accountName}</span>
          </div>
          <p className="h-[12px] w-full text-[8px] leading-tight text-white opacity-80">
            {props.contentText.length > 12 ? props.contentText.substring(0, 12) : props.contentText}
          </p>
          <div className="flex items-center justify-between">
            <MusicIcon fill="#FFFFFF" />
            <span className="ml-1 w-full text-[8px] text-white">Lalalala.music - LIddell</span>
          </div>
        </div>
        <div className="absolute right-[9px] top-[6px] flex h-[411px] items-center justify-center">
          <img src={TiktokFrame} alt="" className="h-full w-full" />
        </div>
      </ContentVideoPlayer>
    </div>
  );
};

const Other = (props: { imagePath: string }) => {
  return (
    <>
      {props.imagePath.includes('mp4') ? (
        <video controls className="h-[287px] w-[290px]" src={props.imagePath}>
          <track kind="captions" />
        </video>
      ) : (
        <img src={props.imagePath} alt="" className="h-[287px] w-[290px]" />
      )}
    </>
  );
};

export const ContentPreview = (props: Props) => {
  const formatDateTime = (dateTime: string): string => {
    const date = new Date(dateTime);
    const year = date.getFullYear();
    const month = date.getMonth() + 1; // 月は0から始まるため+1
    const day = date.getDate();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');

    return `${year}年${month}月${day}日 ${hours}:${minutes}`;
  };
  return (
    <>
      {props.postingType === PostingType.Feed && (
        <InstagramFeed
          imagePath={props.imagePath}
          accountName={props.accountName}
          contentText={props.contentText}
          postingDate={formatDateTime(props.postingDate)}
        />
      )}
      {props.postingType === PostingType.Reel && (
        <InstagramReel
          imagePath={props.imagePath}
          accountName={props.accountName}
          contentText={props.contentText}
          postingDate={formatDateTime(props.postingDate)}
        />
      )}
      {props.postingType === PostingType.Stories && (
        <InstagramStory
          imagePath={props.imagePath}
          accountName={props.accountName}
          contentText={props.contentText}
          postingDate={formatDateTime(props.postingDate)}
        />
      )}
      {props.postingType === PostingType.XImagePost && (
        <XImagePost
          imagePath={props.imagePath}
          accountName={props.accountName}
          contentText={props.contentText}
          postingDate={formatDateTime(props.postingDate)}
        />
      )}
      {props.postingType === PostingType.XPost && (
        <XPost
          imagePath={props.imagePath}
          accountName={props.accountName}
          contentText={props.contentText}
          postingDate={formatDateTime(props.postingDate)}
        />
      )}
      {props.postingType === PostingType.Youtube && (
        <Youtube
          imagePath={props.imagePath}
          accountName={props.accountName}
          contentText={props.contentText}
          postingDate={formatDateTime(props.postingDate)}
          movieUrl={props.movieUrl ?? 'https://www.youtube.com/watch?v=example'}
        />
      )}
      {props.postingType === PostingType.YoutubeShort && (
        <YoutubeShort
          imagePath={props.imagePath}
          accountName={props.accountName}
          contentText={props.contentText}
          postingDate={formatDateTime(props.postingDate)}
        />
      )}
      {props.postingType === PostingType.TikTok && (
        <Tiktok
          imagePath={props.imagePath}
          accountName={props.accountName}
          contentText={props.contentText}
          postingDate={formatDateTime(props.postingDate)}
        />
      )}
      {props.postingType === PostingType.Other && <Other imagePath={props.imagePath} />}
    </>
  );
};
